/**
 * AppDashboardShell — Canonical dashboard shell scaffold.
 * Single source of truth: packages/ui
 *
 * Provides the shared structural skeleton used by Kloutforce, Katalog, Koordinator, and future apps:
 *   - Top navbar (fixed or flow, configurable via fixedHeader)
 *   - Optional left sidebar (collapsible, with mobile overlay)
 *   - Optional secondary navbar row (e.g. KatalogBar)
 *   - Mobile right-side drawers (account, filters, search) via RightDrawerShell
 *   - Desktop right panel via DesktopRightPanel
 *   - DashboardLayoutContext wiring
 *   - AppMain scroll container
 *
 * Apps pass configuration (nav items, sidebar content, panel slots, branding)
 * rather than duplicating the shell DOM.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { DashboardLayoutProvider } from './DashboardLayoutContext';
import { DesktopRightPanel, type RightPanelMode } from './DesktopRightPanel';
import { RightDrawerShell } from './RightDrawerShell';
import { DiscoveryDrawerShell } from '../discover/mobile/DiscoveryDrawerShell';
import { AppMain } from './AppMain';
import { PrimaryActionButton } from '../buttons/PrimaryActionButton';

// ─── Types ───────────────────────────────────────────────────────

export interface NavbarItemConfig {
  label: string;
  path?: string;
  icon: React.ComponentType<{ className?: string }>;
  /** If set, clicking toggles the right panel instead of navigating */
  action?: 'toggle-sidebar';
}

export interface AppDashboardShellProps {
  children: React.ReactNode;

  // ─── Navbar ────────────────────────────────────────────────────
  /** Logo element rendered on the left of the navbar */
  logo: React.ReactNode;
  /** Primary navbar items (Home, Discover, etc.) */
  navItems: NavbarItemConfig[];
  /** Callback when a nav item with a `path` is clicked */
  onNavigate: (path: string) => void;
  /** Check if a path is currently active */
  isPathActive: (path: string) => boolean;

  // ─── Header behavior ──────────────────────────────────────────
  /**
   * Whether the header is fixed (position: fixed + pt offset on content).
   * Default: true (kloutforce/katalog behavior).
   * Set to false for flow-layout header (koordinator behavior).
   */
  fixedHeader?: boolean;

  // ─── Secondary navbar row ─────────────────────────────────────
  /** Optional second row below the navbar (e.g. KatalogBar) */
  secondaryNavbar?: React.ReactNode;

  // ─── Left sidebar ─────────────────────────────────────────────
  /** Content rendered inside the collapsible left sidebar */
  sidebar?: React.ReactNode;
  /** Whether the left sidebar is currently open (app manages state) */
  sidebarOpen?: boolean;
  /** Called when the mobile sidebar overlay is clicked to close */
  onSidebarClose?: () => void;

  // ─── Account sidebar ──────────────────────────────────────────
  /** Content rendered inside the account drawer/panel */
  accountContent?: React.ReactNode;

  // ─── Right panel slots ────────────────────────────────────────
  /** Content for the filters drawer/panel. Receives current panel mode. */
  filtersContent?: React.ReactNode;
  /** Content for the search drawer/panel. Receives current panel mode. */
  searchContent?: React.ReactNode;
  /** Content for the Agent C drawer/panel (Kloutsale only). */
  agentCContent?: React.ReactNode;

  // ─── Right panel metadata ─────────────────────────────────────
  /** Title forwarded to DesktopRightPanel (e.g. user name for account) */
  rightPanelTitle?: string;

  // ─── Main area ────────────────────────────────────────────────
  /** Extra className for AppMain (e.g. 'bg-katalog-surface') */
  mainClassName?: string;

  // ─── Overrides ────────────────────────────────────────────────
  /** Override the initial right panel state */
  defaultRightPanel?: RightPanelMode;
  /** Controlled right panel mode (if parent manages state) */
  rightPanel?: RightPanelMode;
  /** Controlled setter (if parent manages state) */
  onRightPanelChange?: (panel: RightPanelMode) => void;

  /** Root className override */
  className?: string;

  /** CSS var for header offset — default '56px' */
  headerOffset?: string;
  /** Preserve app scrolling by default; page mode is an explicit route opt-in. */
  viewportMode?: 'page' | 'app';
}

export const AppDashboardShell: React.FC<AppDashboardShellProps> = ({
  children,
  logo,
  navItems,
  onNavigate,
  isPathActive,
  fixedHeader = true,
  secondaryNavbar,
  sidebar,
  sidebarOpen = false,
  onSidebarClose,
  accountContent,
  filtersContent,
  searchContent,
  agentCContent,
  rightPanelTitle,
  mainClassName,
  defaultRightPanel = 'none',
  rightPanel: controlledPanel,
  onRightPanelChange,
  className,
  headerOffset,
  viewportMode = 'app',
}) => {
  // ─── Right panel state (uncontrolled or controlled) ───────────
  const [internalPanel, setInternalPanel] = useState<RightPanelMode>(defaultRightPanel);
  const isControlled = controlledPanel !== undefined;
  const rightPanel = isControlled ? controlledPanel : internalPanel;
  const setRightPanel = useCallback(
    (panel: RightPanelMode) => {
      if (isControlled && onRightPanelChange) {
        onRightPanelChange(panel);
      } else {
        setInternalPanel(panel);
      }
    },
    [isControlled, onRightPanelChange],
  );

  const closeRightPanel = useCallback(() => setRightPanel('none'), [setRightPanel]);

  // ─── Navbar click handler ─────────────────────────────────────
  const handleNavbarClick = useCallback(
    (item: NavbarItemConfig) => {
      if (item.action === 'toggle-sidebar') {
        setRightPanel(rightPanel === 'account' ? 'none' : 'account');
      } else if (item.path) {
        setRightPanel('none');
        onNavigate(item.path);
      }
    },
    [rightPanel, setRightPanel, onNavigate],
  );

  // ─── Compute header offset ────────────────────────────────────
  const hdrValue = headerOffset ?? (secondaryNavbar ? '112px' : '56px');

  // ─── Context ──────────────────────────────────────────────────
  const layoutContextValue = useMemo(
    () => ({ sidebarOpen, rightPanel, setRightPanel }),
    [sidebarOpen, rightPanel, setRightPanel],
  );

  return (
    <DashboardLayoutProvider value={layoutContextValue}>
      <div
        className={cn(
          viewportMode === 'app'
            ? 'h-[100dvh] bg-background flex flex-col w-full overflow-hidden'
            : 'min-h-[100dvh] bg-background flex flex-col w-full overflow-x-clip',
          className,
        )}
      >
        {/* ═══ Top Navbar ═══ */}
        <header
          className={cn(
            'bg-card border-b border-border z-50',
            fixedHeader ? 'fixed inset-x-0 top-0' : 'shrink-0',
          )}
        >
          {/* Row 1: Main navbar */}
          <div className="h-14 flex items-center justify-between px-4 md:px-6">
            {/* Logo */}
            {logo}

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.path ? isPathActive(item.path) : false;
                const isAccountActive =
                  item.action === 'toggle-sidebar' && rightPanel === 'account';
                return (
                  <button
                    key={item.label}
                    onClick={() => handleNavbarClick(item)}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive || isAccountActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    )}
                    aria-label={
                      item.action === 'toggle-sidebar'
                        ? `${item.label} - ${rightPanel === 'account' ? 'close' : 'open'} panel`
                        : item.label
                    }
                    aria-expanded={
                      item.action === 'toggle-sidebar' ? rightPanel === 'account' : undefined
                    }
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </nav>

            {/* Mobile nav */}
            <nav className="flex md:hidden items-center gap-4" aria-label="Mobile navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.path ? isPathActive(item.path) : false;
                const isAccountActive =
                  item.action === 'toggle-sidebar' && rightPanel === 'account';
                return (
                  <button
                    key={item.label}
                    onClick={() => handleNavbarClick(item)}
                    className={cn(
                      'p-3 rounded-xl transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center',
                      isActive || isAccountActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    )}
                    aria-label={item.label}
                    aria-expanded={
                      item.action === 'toggle-sidebar' ? rightPanel === 'account' : undefined
                    }
                  >
                    <Icon className="w-6 h-6" />
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Row 2: Optional secondary navbar */}
          {secondaryNavbar}
        </header>

        {/* ═══ Content Area ═══ */}
        <div
          className={cn(
            'bw-layout-shell flex flex-1 w-full relative min-w-0 min-h-0',
            fixedHeader && 'pt-14',
          )}
          style={{ ['--hdr' as string]: hdrValue }}
        >
          {/* Mobile sidebar overlay */}
          {sidebar && sidebarOpen && onSidebarClose && (
            <div
              className="fixed inset-0 bg-black/30 z-30 md:hidden"
              onClick={onSidebarClose}
              aria-hidden="true"
            />
          )}

          {/* Desktop left sidebar */}
          {sidebar && (
            <aside
              className={cn(
                'hidden md:flex flex-col border-r border-border bg-card transition-all duration-300 shrink-0 sticky overflow-hidden',
                viewportMode === 'page' && fixedHeader
                  ? 'top-14 h-[calc(100dvh-3.5rem)]'
                  : 'top-0 h-[calc(100dvh-3.5rem)]',
                sidebarOpen ? 'w-64' : 'w-0 border-r-0',
              )}
              aria-label="Sidebar"
            >
              <div className="flex-1 overflow-y-auto overflow-x-hidden h-full">
                {sidebarOpen && sidebar}
              </div>
            </aside>
          )}

          {/* Mobile Account Drawer */}
          {accountContent && (
            <RightDrawerShell
              open={rightPanel === 'account'}
              variant="account"
              onClose={closeRightPanel}
              title={rightPanelTitle}
            >
              {accountContent}
            </RightDrawerShell>
          )}

          {/* Mobile Filters Drawer */}
          {filtersContent && (
            <RightDrawerShell
              open={rightPanel === 'filters'}
              variant="filters"
              onClose={closeRightPanel}
              footer={
                <div className="p-4">
                  <PrimaryActionButton
                    onClick={() => {
                      const root = document.querySelector(
                        '[data-right-drawer-shell="true"][data-variant="filters"]',
                      );
                      root?.querySelector<HTMLElement>('[data-panel-apply="true"]')?.click();
                      closeRightPanel();
                    }}
                  >
                    APPLY
                  </PrimaryActionButton>
                </div>
              }
            >
              {filtersContent}
            </RightDrawerShell>
          )}

          {/* Mobile Search Drawer */}
          {searchContent && (
            <RightDrawerShell
              open={rightPanel === 'search'}
              variant="search"
              onClose={closeRightPanel}
              footer={
                <div className="p-4">
                  <PrimaryActionButton
                    onClick={() => {
                      const root = document.querySelector(
                        '[data-right-drawer-shell="true"][data-variant="search"]',
                      );
                      root?.querySelector<HTMLElement>('[data-panel-search="true"]')?.click();
                      closeRightPanel();
                    }}
                  >
                    Search
                  </PrimaryActionButton>
                </div>
              }
            >
              {searchContent}
            </RightDrawerShell>
          )}

          {/* Mobile Agent C Drawer — canonical bottom sheet */}
          {agentCContent && (
            <DiscoveryDrawerShell
              open={rightPanel === 'agent-c'}
              onClose={closeRightPanel}
              mode="search"
              presentation="bottom-sheet"
              headerCenter={
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-foreground">Agent C</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                    Intelligence
                  </span>
                </div>
              }
            >
              {agentCContent}
            </DiscoveryDrawerShell>
          )}

          {/* Main scroll container */}
          <AppMain className={mainClassName} documentScroll={viewportMode === 'page'}>
            {children}
          </AppMain>

          {/* Desktop Right Panel */}
          <DesktopRightPanel
            panel={rightPanel}
            onClose={closeRightPanel}
            onSwitchPanel={setRightPanel}
            title={rightPanelTitle}
          >
            {rightPanel === 'account' && accountContent}
            {rightPanel === 'filters' && filtersContent}
            {rightPanel === 'search' && searchContent}
            {rightPanel === 'agent-c' && agentCContent}
          </DesktopRightPanel>
        </div>
      </div>
    </DashboardLayoutProvider>
  );
};

AppDashboardShell.displayName = 'AppDashboardShell';
